import React, { useState } from 'react';
import logo from 'media/pngs/logo.png';
import SVG from 'components/renderSvg';
import InputField from 'components/inputField';
import Button from 'components/button';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import emailIcon from '../../../media/svgs/email.svg';
import passwordIcon from '../../../media/svgs/password.svg';
import * as yup from 'yup';
import toast from 'react-hot-toast';
import { forgotPassword, verifyOTP, resetPassword } from '../../../services/vendorAPI';

const STEPS = {
  EMAIL: 'EMAIL',
  OTP: 'OTP',
  PASSWORD: 'PASSWORD',
};

const emailSchema = yup.object().shape({
  email: yup.string().email('Invalid email').required('Email is required'),
});

const otpSchema = yup.object().shape({
  otp: yup.string().length(6, 'OTP must be 6 digits').required('OTP is required'),
});

const passwordSchema = yup.object().shape({
  password: yup.string().min(8, 'Password must be at least 8 characters').required('Password is required'),
  confirmPassword: yup.string()
    .oneOf([yup.ref('password'), null], 'Passwords must match')
    .required('Confirm Password is required'),
});

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [loader, setLoader] = useState(false);
  const [currentStep, setCurrentStep] = useState(STEPS.EMAIL);
  const [userEmail, setUserEmail] = useState('');

  const emailForm = useForm({
    resolver: yupResolver(emailSchema),
    defaultValues: { email: '' },
  });

  const otpForm = useForm({
    resolver: yupResolver(otpSchema),
    defaultValues: { otp: '' },
  });

  const passwordForm = useForm({
    resolver: yupResolver(passwordSchema),
    defaultValues: { password: '', confirmPassword: '' },
  });

  const handleSendOTP = async data => {
    setLoader(true);
    try {
      const response = await forgotPassword(data.email);
      if (response.success) {
        toast.success('OTP sent to your email.');
        setUserEmail(data.email);
        setCurrentStep(STEPS.OTP);
      } else {
        toast.error(response.error || 'Failed to send OTP.');
      }
    } catch (error) {
      toast.error(error?.message || 'Something went wrong.');
    } finally {
      setLoader(false);
    }
  };

  const handleVerifyOTP = async data => {
    setLoader(true);
    try {
      const response = await verifyOTP(userEmail, data.otp);
      if (response.success) {
        toast.success('OTP verified successfully!');
        setCurrentStep(STEPS.PASSWORD);
      } else {
        toast.error(response.error || 'Invalid or expired OTP.');
      }
    } catch (error) {
      toast.error(error?.message || 'Something went wrong.');
    } finally {
      setLoader(false);
    }
  };

  const handleResetPassword = async data => {
    setLoader(true);
    try {
      const response = await resetPassword(userEmail, data.password);
      if (response.success) {
        toast.success('Password reset successfully! Please login.');
        navigate('/login');
      } else {
        toast.error(response.error || 'Failed to reset password.');
      }
    } catch (error) {
      toast.error(error?.message || 'Something went wrong.');
    } finally {
      setLoader(false);
    }
  };

  return (
    <div className='flex justify-center items-center h-screen flex-col bg-[#f7f7f8]'>
      <div className='flex flex-col items-center gap-1 mb-6'>
        <img src={logo} alt='logo' className="mb-4" />
        <h1 className='text-primary font-medium text-[24px]'>
          {currentStep === STEPS.EMAIL && 'Forgot Password'}
          {currentStep === STEPS.OTP && 'Verify OTP'}
          {currentStep === STEPS.PASSWORD && 'Create New Password'}
        </h1>
        <p className='text-[#3f3f50] font-normal text-[16px] text-center max-w-[400px]'>
          {currentStep === STEPS.EMAIL && 'Enter your registered email to receive a 6-digit verification code.'}
          {currentStep === STEPS.OTP && `We've sent a 6-digit code to ${userEmail}. Please enter it below.`}
          {currentStep === STEPS.PASSWORD && 'Please enter a new strong password for your account.'}
        </p>
      </div>

      <div className='bg-white shadow-sm p-8 rounded-[16px] md:w-[500px] xs:w-full sm:w-full'>
        {currentStep === STEPS.EMAIL && (
          <form onSubmit={emailForm.handleSubmit(handleSendOTP)}>
            <InputField
              type='email'
              name='email'
              label='Email Address'
              placeholder='example@gmail.com'
              error={emailForm.formState.errors?.email?.message}
              register={emailForm.register}
              icon={<SVG icon={emailIcon} />}
            />
            <Button
              title='Send Verification Code'
              variant='success'
              className='w-full mt-4'
              disabled={loader}
            />
            <div className='mt-4 text-center'>
              <span
                className='text-primary cursor-pointer underline font-medium'
                onClick={() => navigate('/login')}
              >
                Back to Login
              </span>
            </div>
          </form>
        )}

        {currentStep === STEPS.OTP && (
          <form onSubmit={otpForm.handleSubmit(handleVerifyOTP)}>
            <InputField
              type='text'
              name='otp'
              label='Verification Code'
              placeholder='Enter 6-digit OTP'
              maxLength={6}
              error={otpForm.formState.errors?.otp?.message}
              register={otpForm.register}
              icon={<SVG icon={passwordIcon} />}
            />
            <Button
              title='Verify OTP'
              variant='success'
              className='w-full mt-4'
              disabled={loader}
            />
            <div className='mt-4 text-center'>
              <span
                className='text-primary cursor-pointer underline font-medium'
                onClick={() => setCurrentStep(STEPS.EMAIL)}
              >
                Change Email
              </span>
            </div>
          </form>
        )}

        {currentStep === STEPS.PASSWORD && (
          <form onSubmit={passwordForm.handleSubmit(handleResetPassword)}>
            <InputField
              type='password'
              name='password'
              label='New Password'
              placeholder='Enter new password'
              error={passwordForm.formState.errors?.password?.message}
              register={passwordForm.register}
              icon={<SVG icon={passwordIcon} />}
            />
            <InputField
              type='password'
              name='confirmPassword'
              label='Confirm New Password'
              placeholder='Confirm your new password'
              error={passwordForm.formState.errors?.confirmPassword?.message}
              register={passwordForm.register}
              icon={<SVG icon={passwordIcon} />}
            />
            <Button
              title='Reset Password'
              variant='success'
              className='w-full mt-4'
              disabled={loader}
            />
          </form>
        )}
      </div>
    </div>
  );
};

export default ForgotPasswordPage;
